"use client";
import React, { useEffect, useMemo, useState } from "react";
import { Image } from "antd";
import { useTranslation } from "react-i18next";
import { useMessaging } from "../../context/MessagingContext";
import "./ChatHeader.css";

import { resolveDoctorAvatar } from "../../../ProfileDoctor/hooks/useDoctorInformation";

const DEFAULT_AVATAR = "https://cdn-icons-png.flaticon.com/512/149/149071.png";

const getInitials = (firstName, lastName) => {
  const first = firstName?.[0] || "";
  const last = lastName?.[0] || "";
  return (first + last).toUpperCase() || "?";
};

// ✅ FIX: accepte contactOverride en prop pour éviter la désynchronisation avec le contexte
const ChatHeader = ({ onAttach, restriction, contactOverride }) => {
  const { t } = useTranslation();
  const { selectedContact: ctxContact, toggleProfile, selectContact } = useMessaging();
  const [isOnline, setIsOnline] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  // Close the moderation menu when switching contact.
  useEffect(() => setMenuOpen(false), [contactOverride?.id, ctxContact?.id]);

  // ✅ FIX: priorité à la prop (vient de ChatWindow qui a le bon contact)
  // puis fallback sur le contexte
  const selectedContact = contactOverride || ctxContact;

  useEffect(() => {
    if (selectedContact) setIsOnline(!!selectedContact.isOnline);
  }, [selectedContact]);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const handleAttachClick = () => {
    if (toggleProfile) toggleProfile();
    if (onAttach) onAttach();
  };

  const handleBackClick = () => {
    if (selectContact) selectContact(null);
  };

  if (!selectedContact) {
    return (
      <div className="chat-header chat-header--empty">
        <div className="ch-avatar ch-avatar--placeholder">?</div>
        <div className="ch-user">
          <div className="ch-name">{t("messaging.chatHeader.noContactTitle")}</div>
          <div className="ch-role">{t("messaging.chatHeader.noContactSubtitle")}</div>
        </div>
      </div>
    );
  }

  // ✅ FIX: ordre de priorité étendu pour récupérer le nom
  const name =
    selectedContact.fullName ||
    selectedContact.name ||
    selectedContact.meta?.displayName ||
    `${selectedContact.firstName || ""} ${selectedContact.lastName || ""}`.trim() ||
    selectedContact.meta?.email ||
    selectedContact.email ||
    "—";

  const firstName =
    selectedContact.firstName ||
    selectedContact.meta?.firstName ||
    name.split(" ")[0] ||
    "";
  const lastName =
    selectedContact.lastName ||
    selectedContact.meta?.lastName ||
    name.split(" ").slice(1).join(" ") ||
    "";

  const normalizeSpecialtyKey = (value) => {
    if (!value) return null;
    const norm = value.toString().trim().toLowerCase();
    if (!norm) return null;
    if (norm.includes("dermatolog")) return "dermatology";
    if (norm.includes("pediatr")) return "pediatrics";
    if (norm.includes("cardiolog")) return "cardiology";
    if (norm.includes("neurolog")) return "neurology";
    if (norm.includes("psychiatr")) return "psychiatry";
    if (norm.includes("ophthalmolog") || norm.includes("ophtalmolog")) return "ophthalmology";
    if (norm.includes("orthop")) return "orthopedics";
    if (norm.includes("gynecolog") || norm.includes("gynaecolog")) return "gynecology";
    if (norm.includes("dentist")) return "dentistry";
    if (norm.includes("general")) return "general";
    if (norm === "ent" || (norm.includes("ear") && norm.includes("nose") && norm.includes("throat"))) return "ent";
    return null;
  };

  const specialtyKey = normalizeSpecialtyKey(
    selectedContact.meta?.specialty || selectedContact.specialty
  );
  const rawRole = selectedContact.meta?.role;

  let role;
  if (specialtyKey) {
    role = t(`doctorFinder.specialties.${specialtyKey}`);
  } else if (rawRole) {
    const normRole = String(rawRole).toLowerCase();
    if (normRole.includes("patient")) role = t("messaging.contacts.patientRole");
    else role = rawRole;
  } else {
    role = t("messaging.contacts.defaultDoctorSpecialty");
  }

  const isPatient = useMemo(() => {
    const rr = String(rawRole || "").toLowerCase().trim();
    if (rr.includes("patient")) return true;
    if (rr.includes("doctor") || rr.includes("dr") || rr.includes("médecin") || rr.includes("medecin")) return false;
    if (specialtyKey) return false;
    return false;
  }, [rawRole, specialtyKey]);

  const rawAvatar =
    selectedContact.avatar ||
    selectedContact.avatarUrl ||
    selectedContact.photo ||
    selectedContact.meta?.avatarUrl ||
    selectedContact.meta?.avatar ||
    selectedContact.meta?.photo ||
    "";

  const isDoctorContact = useMemo(() => {
    const rr = String(rawRole || "").toLowerCase().trim();
    if (rr.includes("doctor") || rr.includes("dr") || rr.includes("médecin") || rr.includes("medecin")) return true;
    if (specialtyKey) return true;
    return false;
  }, [rawRole, specialtyKey]);

  const avatarSrc = useMemo(() => {
    if (!rawAvatar) return undefined;
    return isDoctorContact ? resolveDoctorAvatar(rawAvatar) : rawAvatar;
  }, [rawAvatar, isDoctorContact]);

  return (
    <div className="chat-header">
      {isMobile && (
        <button
          className="ch-back-btn"
          onClick={handleBackClick}
          aria-label={t("messaging.chatHeader.backAria")}
          title={t("messaging.chatHeader.backTitle")}
        >
          ←
        </button>
      )}

      <div className="ch-avatarwrap">
        {avatarSrc ? (
          <Image
            src={avatarSrc}
            width={42}
            height={42}
            preview={false}
            fallback={DEFAULT_AVATAR}
            className="ch-avatarimg"
            style={{ borderRadius: "50%", objectFit: "cover" }}
          />
        ) : (
          <div
            className="ch-avatar--placeholder"
            style={{
              background: "#2563eb",
              color: "#fff",
              display: "grid",
              placeItems: "center",
              fontWeight: "700",
              fontSize: "18px",
              borderRadius: "50%",
              width: "42px",
              height: "42px",
            }}
          >
            {getInitials(firstName, lastName)}
          </div>
        )}
      </div>

      <div className="ch-user">
        <div className="ch-name" title={name}>
          {name}
        </div>
        <div className="ch-role" title={role}>
          {role}
        </div>
      </div>

      <div className="ch-actions">
        {restriction && (
          <div className="ch-restrict-menu">
            <button
              type="button"
              className={`h-iconbtn h-iconbtn--restriction ${
                restriction.status.blockedByMe || restriction.status.restrictedByMe ? "h-iconbtn--active" : ""
              }`}
              aria-label={t("chatWindow.moderation")}
              title={
                restriction.status.blockedByMe
                  ? t("chatWindow.blockedByMe")
                  : restriction.status.restrictedByMe
                  ? t("chatWindow.restrictedByMe")
                  : t("chatWindow.moderation")
              }
              onClick={() => setMenuOpen((v) => !v)}
              disabled={restriction.busy}
            >
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d={restriction.status.blockedByMe ? "M7 11V7a5 5 0 0 1 10 0v4" : "M7 11V7a5 5 0 0 1 9.9-1"} />
              </svg>
            </button>

            {menuOpen && (
              <div className="ch-restrict-menu__list" role="menu">
                {restriction.status.blockedByMe ? (
                  <button type="button" role="menuitem" onClick={() => { setMenuOpen(false); restriction.onClear(); }}>
                    {t("chatWindow.unblock")}
                  </button>
                ) : (
                  <>
                    {restriction.status.restrictedByMe ? (
                      <button type="button" role="menuitem" onClick={() => { setMenuOpen(false); restriction.onClear(); }}>
                        {t("chatWindow.unrestrict")}
                      </button>
                    ) : (
                      <button type="button" role="menuitem" onClick={() => { setMenuOpen(false); restriction.onRestrict(); }}>
                        {t("chatWindow.restrict")}
                      </button>
                    )}
                    <button type="button" role="menuitem" className="is-danger" onClick={() => { setMenuOpen(false); restriction.onBlock(); }}>
                      {t("chatWindow.block")}
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        )}

        <button
          className="h-iconbtn"
          aria-label={t("messaging.chatHeader.attachAria")}
          onClick={handleAttachClick}
          title={t("messaging.chatHeader.attachTitle")}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
            <path d="M21.44 11.05l-9.19 9.19a5 5 0 0 1-7.07-7.07l9.19-9.19a3 3 0 0 1 4.24 4.24l-9.19 9.19a1 1 0 0 1-1.41-1.41l8.48-8.48" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default ChatHeader;